import { useState } from "react";
import { stores } from "../data/mockData";

const storeTypes = ["All", "Supermarket", "Fresh Produce", "Convenience", "Specialty"];

export default function StoreLocator() {
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  const filtered = filter === "All" ? stores : stores.filter((s) => s.type === filter);

  const renderStars = (rating) => {
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    return (
      <span style={styles.stars}>
        {"★".repeat(full)}{half ? "½" : ""}{"☆".repeat(5 - full - (half ? 1 : 0))}
        <span style={styles.ratingNum}> {rating}</span>
      </span>
    );
  };

  return (
    <div className="page">
      <h1 className="page-title">📍 Nearby Stores</h1>
      <p className="page-subtitle">Find the best places to pick up your groceries.</p>

      {/* Illustrated map placeholder */}
      <div style={styles.mapBox}>
        <div style={styles.mapGrid}>
          {stores.map((s) => (
            <div
              key={s.id}
              style={{
                ...styles.mapMarker,
                ...(selected?.id === s.id ? styles.markerActive : {}),
                left: `${10 + (s.id - 1) * 18}%`,
                top: `${20 + ((s.id % 3) * 25)}%`,
              }}
              onClick={() => setSelected(s)}
              title={s.name}
            >
              {s.emoji}
              <div style={styles.markerLabel}>{s.name.split(" ")[0]}</div>
            </div>
          ))}

          {/* Road lines */}
          <svg style={styles.mapSvg} viewBox="0 0 100 100" preserveAspectRatio="none">
            <line x1="0" y1="50" x2="100" y2="50" stroke="#e0cdb0" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="50" y1="0" x2="50" y2="100" stroke="#e0cdb0" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="20" y1="0" x2="80" y2="100" stroke="#e0cdb0" strokeWidth="0.3" strokeDasharray="2,2" />
          </svg>

          <div style={styles.mapLabel}>📍 Your Location</div>
          <div style={styles.youPin}>🏠</div>
        </div>

        {/* Selected popup */}
        {selected && (
          <div style={styles.mapPopup}>
            <button style={styles.closePopup} onClick={() => setSelected(null)}>✕</button>
            <div style={styles.popupEmoji}>{selected.emoji}</div>
            <div style={styles.popupName}>{selected.name}</div>
            <div style={styles.popupMeta}>{selected.distance} away</div>
            {renderStars(selected.rating)}
            <a
              href={`https://www.google.com/maps/search/${encodeURIComponent(selected.name)}`}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              style={styles.dirBtn}
            >
              Get Directions 🗺️
            </a>
          </div>
        )}
      </div>

      {/* Filter row */}
      <div style={styles.filters}>
        {storeTypes.map((t) => (
          <button key={t} onClick={() => setFilter(t)} style={{ ...styles.filterBtn, ...(filter === t ? styles.filterActive : {}) }}>
            {t}
          </button>
        ))}
      </div>

      {/* Store list */}
      <div style={styles.list}>
        {filtered.map((store) => (
          <div
            key={store.id}
            className="card"
            style={{ ...styles.storeCard, ...(selected?.id === store.id ? styles.storeSelected : {}) }}
            onClick={() => setSelected(store.id === selected?.id ? null : store)}
          >
            <div style={styles.storeEmoji}>{store.emoji}</div>
            <div style={styles.storeInfo}>
              <div style={styles.storeName}>{store.name}</div>
              <div style={styles.storeMeta}>
                <span className="tag">{store.type}</span>
                <span style={styles.distBadge}>📍 {store.distance}</span>
              </div>
              {renderStars(store.rating)}
            </div>
            <a
              href={`https://www.google.com/maps/search/${encodeURIComponent(store.name)}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={styles.dirLink}
            >
              🗺️ Directions
            </a>
          </div>
        ))}
      </div>

      {/* Note */}
      <div style={styles.note}>
        💡 For live location and store data, enable Google Maps in your browser settings.
      </div>
    </div>
  );
}

const styles = {
  mapBox: {
    background: "#f5efe4",
    border: "1.5px solid #e0cdb0",
    borderRadius: "16px",
    position: "relative",
    height: "260px",
    marginBottom: "1.5rem",
    overflow: "hidden",
  },
  mapGrid: { width: "100%", height: "100%", position: "relative" },
  mapSvg: { position: "absolute", inset: 0, width: "100%", height: "100%" },
  mapMarker: {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    fontSize: "1.6rem",
    transition: "transform 0.2s",
    zIndex: 2,
  },
  markerActive: { transform: "scale(1.3)" },
  markerLabel: {
    background: "#fff9f2",
    border: "1px solid #e0cdb0",
    borderRadius: "4px",
    padding: "0.1rem 0.4rem",
    fontSize: "0.65rem",
    color: "#6b4f2e",
    fontWeight: 700,
    marginTop: "2px",
    whiteSpace: "nowrap",
  },
  mapLabel: { position: "absolute", bottom: "12px", left: "50%", transform: "translateX(-50%)", fontSize: "0.75rem", color: "#9b7b54", fontWeight: 700 },
  youPin: { position: "absolute", bottom: "30px", left: "50%", transform: "translateX(-50%)", fontSize: "1.4rem" },
  mapPopup: {
    position: "absolute",
    top: "12px",
    right: "12px",
    background: "#fff9f2",
    border: "1.5px solid #e0cdb0",
    borderRadius: "12px",
    padding: "1rem",
    boxShadow: "0 4px 16px rgba(107,79,46,0.15)",
    minWidth: "180px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.3rem",
    zIndex: 10,
  },
  closePopup: { position: "absolute", top: "6px", right: "8px", background: "none", border: "none", color: "#c4a882", cursor: "pointer", fontSize: "0.9rem" },
  popupEmoji: { fontSize: "2rem" },
  popupName: { fontFamily: "'Playfair Display', serif", fontSize: "0.9rem", color: "#3d2b1a", textAlign: "center", fontWeight: 600 },
  popupMeta: { fontSize: "0.78rem", color: "#9b7b54" },
  dirBtn: { marginTop: "0.4rem", fontSize: "0.78rem", padding: "0.4rem 0.9rem", textDecoration: "none", background: "#9b7b54", color: "#fff9f2", borderRadius: "50px", fontWeight: 700 },

  filters: { display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.25rem" },
  filterBtn: { padding: "0.35rem 0.9rem", borderRadius: "50px", border: "1.5px solid #e0cdb0", background: "#fff9f2", color: "#9b7b54", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", transition: "all 0.2s" },
  filterActive: { background: "#f0e8d8", color: "#6b4f2e", borderColor: "#c4a882" },

  list: { display: "flex", flexDirection: "column", gap: "0.85rem" },
  storeCard: { display: "flex", alignItems: "center", gap: "1rem", cursor: "pointer" },
  storeSelected: { borderColor: "#c4a882", background: "#fdf8f0" },
  storeEmoji: { fontSize: "2.2rem", flexShrink: 0 },
  storeInfo: { flex: 1, display: "flex", flexDirection: "column", gap: "0.3rem" },
  storeName: { fontFamily: "'Playfair Display', serif", fontSize: "1rem", color: "#3d2b1a" },
  storeMeta: { display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" },
  distBadge: { fontSize: "0.8rem", color: "#9b7b54", fontWeight: 700 },
  stars: { color: "#c4a882", fontSize: "0.85rem" },
  ratingNum: { color: "#9b7b54", fontWeight: 700 },
  dirLink: { color: "#9b7b54", fontWeight: 700, fontSize: "0.85rem", border: "1.5px solid #e0cdb0", padding: "0.35rem 0.75rem", borderRadius: "50px", whiteSpace: "nowrap", textDecoration: "none" },

  note: { marginTop: "1.5rem", background: "#f0e8d8", borderRadius: "10px", padding: "0.85rem 1.1rem", fontSize: "0.85rem", color: "#9b7b54" },
};
